import { useEffect, useRef, useState } from 'react';
import { useDrag, useDragLayer } from 'react-dnd';
import { useLocation } from 'react-router-dom';
import { AnyObject } from 'yup';
import { getDefaultSizeSealStamp } from '../../../api/sealStamp';
import { dndItemTypes } from '../../../constants/dndType';
import { DndSign } from '../../../model/sign';
import SysConfigStore from '../../../store/common/SysConfigStore';

type Props = {
	id: string | number;
	name?: string;
	imageUrl?: string;
	canDrag: boolean;
	onDrop: (item: AnyObject, dropResult: AnyObject) => void;
	onImageLoad?: (id: string | number, width: number, height: number) => void;
};

type DndSignWithZoom = DndSign & { viewerZoom?: number };

// 드래그할 때만 보이는 미리보기 컴포넌트
function DragPreview({
	imageUrl,
	signId,
	originWidth,
	originHeight,
}: {
	imageUrl: string;
	signId: number;
	originWidth: number;
	originHeight: number;
}) {
	const { currentOffset, isDragging, item } = useDragLayer(monitor => ({
		currentOffset: monitor.getSourceClientOffset(),
		isDragging: monitor.isDragging(),
		item: monitor.getItem() as DndSignWithZoom,
	}));

	const { isComponentEnabled } = SysConfigStore();
	const [size, setSize] = useState({ width: 100, height: 100 });

	const getCurrentZoomFromDOM = (): number => {
		const zoomEl = document.querySelector('[data-testid="zoom__popover-target-scale"]');
		if (!zoomEl) return 1;

		const text = zoomEl.textContent?.replace('%', '');
		const scale = Number(text);
		return Number.isNaN(scale) ? 1 : scale / 100;
	};

	const zoom = getCurrentZoomFromDOM();

	const location = useLocation();
	const isTestSealPage = location.pathname === '/sign/test-contract-seal';

	let previewWidth = size.width;
	let previewHeight = size.height;

	if (!isTestSealPage) {
		previewWidth *= zoom;
		previewHeight *= zoom;
	}

	useEffect(() => {
		const fetchSize = async () => {
			const res = await getDefaultSizeSealStamp(signId, originWidth, originHeight, isComponentEnabled);
			setSize({ width: res.width, height: res.height });
		};

		fetchSize();
	}, [signId]);

	if (!isDragging || !currentOffset) return null;

	return (
		<div
			style={{
				position: 'fixed',
				left: currentOffset.x,
				top: currentOffset.y,
				width: `${previewWidth}px`,
				height: `${previewHeight}px`,
				opacity: 0.8,
				pointerEvents: 'none',
			}}
		>
			<img
				src={imageUrl}
				alt='drag-preview'
				style={{
					width: '100%',
					height: '100%',
					objectFit: 'contain',
				}}
			/>
		</div>
	);
}

export default function SignItem({ id, name = '', canDrag = false, imageUrl, onDrop, onImageLoad }: Props) {
	const ref = useRef<HTMLDivElement>(null);
	const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null);
	const [size, setSize] = useState<{ width: number; height: number }>({ width: 100, height: 100 });

	const imgRef = useRef<HTMLImageElement>(null);

	useEffect(() => {
		if (imgRef.current && onImageLoad) {
			const { width, height } = imgRef.current;
			onImageLoad(id, width, height);
		}
	}, [imageUrl]);

	const [{ isDragging }, drag, preview] = useDrag(
		() => ({
			type: dndItemTypes.sign,
			item: () => {
				const { width, height } = (ref.current as HTMLElement).getBoundingClientRect();
				setSize({ width, height });
				return { id, name, imageUrl, width, height } as DndSign;
			},
			canDrag: () => {
				return canDrag;
			},
			end: (item, monitor) => {
				const dropResult = monitor.getDropResult();

				const { x: reactX, y: reactY } = (ref.current as HTMLElement).getBoundingClientRect();
				if (!item || !dropResult || !cursor) return;

				const { x, y } = cursor;
				const calcX = x - reactX;
				const calcY = y - reactY;

				onDrop({ ...item, x: calcX, y: calcY }, dropResult);
			},
			collect: monitor => {
				const offset = monitor.getClientOffset();
				if (offset != null) setCursor(offset);
				return {
					isDragging: monitor.isDragging(),
				};
			},
		}),
		[id, ref, cursor],
	);

	preview(new Image());

	drag(ref);

	return (
		<>
			<div
				className={`inline-flex ${
					canDrag
						? 'outline-2 outline-dotted outline-transparent hover:outline-red-700 hover:cursor-grab transition-[outline-color]'
						: 'opacity-30'
				}`}
				ref={ref}
			>
				<img className='pointer-events-none' src={imageUrl} alt={name} ref={imgRef} />
			</div>

			{isDragging && (
				<DragPreview
					imageUrl={imageUrl || ''}
					signId={Number(id)}
					originWidth={Number(size.width)}
					originHeight={Number(size.height)}
				/>
			)}
		</>
	);
}
